/**
 * The international arrivals journal — one row per flight landing in Kazakhstan
 * from abroad, with the ground-handling times that belong to that landing.
 *
 * **It is an index beside the schedule, not a second copy of it.** A row is keyed
 * on the flight and filled a field at a time by whichever integration happens to
 * know that field, then finished by hand for the two times nothing reports. For
 * the flights themselves see `types/flight.ts`.
 *
 * A row exists only when all three hold: the flight is an ARRIVAL, its departure
 * airport is outside Kazakhstan, and it has a scheduled arrival time. `sta` is part
 * of the row's identity, so a flight nobody has published an STA for cannot be
 * indexed — the next message carrying one indexes it then.
 *
 * The identity is the triple `(airportIataCode, flt, sta)`, unique in the database.
 * That is what lets six integrations write one arrival without creating six rows.
 */

/** A row of the journal, as `/international-arrival/filter` returns it. */
export interface InternationalArrival {
  /**
   * The journal row. **This is what `/update` addresses** — not
   * `airportScheduleId`, which is a different number entirely.
   */
  id: number;
  created: string | null;
  updated: string | null;
  /**
   * Whether the row is withdrawn.
   *
   * **Read as `active`, filtered as `isActive`.** The asymmetry is the backend's,
   * and it is the reason `InternationalArrivalCriteria` below spells the filter differently.
   */
  active: boolean;
  /** The Kazakh airport that received the flight. Integration-owned. */
  airportIataCode: string | null;
  flt: string | null;
  /**
   * Where it came from. Usually an IATA code, but a source that could not resolve
   * one leaves the station name — or null.
   */
  orgn: string | null;
  /** Scheduled arrival. Part of the row's identity, and NOT NULL in the database. */
  sta: string | null;
  /** Actual arrival — touchdown. */
  ata: string | null;
  /** On-blocks: when the aircraft reached its stand. Later than `ata` by the taxi. */
  br: string | null;
  /**
   * The six varchar fields.
   *
   * **These are `varchar(100)`, not numbers and not timestamps.** They are typed
   * that way because the two passport-control fields have always been entered by
   * hand and have always accepted free text. Do not assume `ttlPax` parses as an
   * integer or that `firstBag` parses as a date — this app renders them as the
   * strings they are, and anything that needs to compute on them must validate
   * first.
   */
  firstPax: string | null;
  lastPax: string | null;
  firstBag: string | null;
  lastBag: string | null;
  ttlPax: string | null;
  ttlBag: string | null;
  spot: string | null;
  acType: string | null;
  /**
   * The schedule row this arrival belongs to — the way through to the flight, its
   * history and the monitor events that produced it.
   *
   * **Can be null.** The link is `ON DELETE SET NULL`, so a flight removed from the
   * schedule leaves its journal row behind: orphaned, but intact.
   */
  airportScheduleId: number | null;
}

/**
 * The request body for `/international-arrival/filter`.
 *
 * **Withdrawn rows come back by default.** The online table applies
 * `isActive = true` unconditionally; this endpoint does not apply it at all unless
 * it is sent, so a screen that should hide withdrawn rows has to say so.
 */
export interface InternationalArrivalCriteria {
  /** Partial and case-insensitive: matched as `UPPER(field) LIKE %VALUE%`. */
  airportIataCode?: string;
  /** Flight number. `KC` matches KC101 and KC922 alike. */
  flt?: string;
  orgn?: string;
  spot?: string;
  acType?: string;
  /** Exact. Omit for no filter at all — which INCLUDES withdrawn rows. */
  isActive?: boolean;

  /**
   * The date RANGES: both ends inclusive, both optional, `yyyy-MM-dd HH:mm:ss`.
   *
   * Same pairs and same bounds as `/online-table/filter`. **Range on `sta` for a
   * day view** — a range on `ata` or `br` silently excludes rows where that column
   * is still empty, because a flight that has not landed cannot fall inside a range
   * of landing times, so those two narrow to "already happened" as a side effect.
   */
  staStart?: string;
  staEnd?: string;
  ataStart?: string;
  ataEnd?: string;
  brStart?: string;
  brEnd?: string;

  /**
   * The bare fields, each meaning **at or before** — `sta <= value`.
   *
   * What they meant before the ranges existed, and left alone rather than
   * repurposed: turning `sta` into the START of a range would have inverted the
   * filter for every caller already sending it, silently and without an error.
   *
   * This app does not use them. Anything it wants to say is expressible as a pair,
   * and sending both forms means the explicit `staEnd` wins — a precedence nobody
   * should have to remember. They are declared so the shape of the endpoint is
   * recorded, not because there is a caller.
   */
  sta?: string;
  ata?: string;
  br?: string;

  pageNum?: number;
  /**
   * Page size, default 10, no upper cap.
   *
   * **Never send 0.** The row query answers an empty list for it while the count
   * query still reports the real total, so `rows: []` arrives beside a non-zero
   * `rsCount` and a `lastBlock` computed from a page size of 10 — which reads as a
   * server fault and is not one.
   */
  rows?: number;
  /**
   * The field to order by, and the direction.
   *
   * This app uses ONLY this pair, never the `staOrder`/`ataOrder`/`brOrder` trio the
   * endpoint also accepts. Those three take precedence when present, so mixing the
   * two mechanisms makes the effective order depend on which fields happen to be
   * set — and `sta`, `ata` and `br` are all orderable through `sortBy` anyway, so
   * the trio buys nothing but that ambiguity. The one thing it can do and this
   * cannot is order by several fields at once, which no column head can ask for.
   */
  sortBy?: InternationalArrivalSortField;
  sortDirection?: 'ascending' | 'descending';
}

/**
 * What `sortBy` accepts.
 *
 * **A name outside this list is a 500, not a 400** — it goes straight into the
 * query and JPA fails on the unknown attribute. So the type is the guard, and the
 * URL is validated against it before a request is built.
 */
export const INTERNATIONAL_ARRIVAL_SORT_FIELDS = [
  'id',
  'created',
  'updated',
  'isActive',
  'airportIataCode',
  'flt',
  'orgn',
  'sta',
  'ata',
  'br',
  'firstPax',
  'lastPax',
  'firstBag',
  'lastBag',
  'ttlPax',
  'ttlBag',
  'spot',
  'acType',
] as const;

export type InternationalArrivalSortField = (typeof INTERNATIONAL_ARRIVAL_SORT_FIELDS)[number];

export const isInternationalArrivalSortField = (value: unknown): value is InternationalArrivalSortField =>
  typeof value === 'string' && (INTERNATIONAL_ARRIVAL_SORT_FIELDS as readonly string[]).includes(value);

